import { useTranslation } from 'react-i18next';
import type { OrganizationProfile } from '@hero/schema';
import {
  CarrierLegalForm,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@hero/web-components';

export interface CarrierLegalDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Loaded organisation profile (from `useOrgProfileApi`). Owned
   * by the parent so the same query result powers both the card's
   * status badge and the drawer's initial values — keeping the
   * two in sync without an extra fetch.
   */
  profile: OrganizationProfile | undefined;
  /** Fired after a successful save. The drawer closes itself. */
  onSaved?: () => void;
}

/**
 * `CarrierLegalDrawer` — financing-flow wrapper around the shared
 * `CarrierLegalForm`. Edits the same fields as the carrier-
 * dashboard Profile → Legal tab, with the financing-required chip
 * surfaced on every field.
 *
 * The shared form handles all the RHF wiring, the dirty-field-
 * only patch construction, and the `useOrgProfileUpdateApi`
 * mutation. The drawer only adds the financing-themed sheet
 * chrome on top.
 */
export function CarrierLegalDrawer({
  isOpen,
  onClose,
  profile,
  onSaved,
}: CarrierLegalDrawerProps) {
  const { t } = useTranslation(['m-financing']);

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col overflow-y-auto p-0 sm:max-w-xl">
        <SheetHeader className="sticky top-0 z-10 border-b bg-white px-6 py-4">
          <SheetTitle>{t('m-financing:requirements.carrier_legal.title')}</SheetTitle>
          <SheetDescription>
            {t('m-financing:requirements.carrier_legal.description')}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 p-6">
          <CarrierLegalForm
            profile={profile}
            displayFor="financing"
            onSaved={() => {
              onSaved?.();
              onClose();
            }}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
